
import { CheckCircle2, XCircle, CircleHelp } from "lucide-react";

interface BooleanIndicatorConfig {
  title?: string;
  value?: number | string | boolean | null;
  active_value?: 0 | 1 | number;
  active_label?: string;
  inactive_label?: string;
  active_color?: string;
  inactive_color?: string;
  device_label?: {
    label?: string;
    unit?: string;
  } | null;
  timestamp?: string | null;
}

interface BooleanIndicatorComponentProps {
  config: BooleanIndicatorConfig;
}

function normalizeBooleanValue(
  value: number | string | boolean | null | undefined
): number | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "boolean") {
    return value ? 1 : 0;
  }

  const parsed = Number(value);

  if (parsed === 0 || parsed === 1) {
    return parsed;
  }

  return null;
}

export function BooleanIndicatorComponent({
  config,
}: BooleanIndicatorComponentProps) {
  const value = normalizeBooleanValue(config.value);

  const activeValue = config.active_value === 0 ? 0 : 1;
  const isActive = value !== null && value === activeValue;

  const activeLabel = config.active_label || "ON";
  const inactiveLabel = config.inactive_label || "OFF";

  const activeColor = config.active_color || "#22c55e";
  const inactiveColor = config.inactive_color || "#ff6258";
  const noDataColor = "#9ca3af";

  const statusColor =
    value === null
      ? noDataColor
      : isActive
        ? activeColor
        : inactiveColor;

  const statusLabel =
    value === null
      ? "NO DATA"
      : isActive
        ? activeLabel
        : inactiveLabel;

  const StatusIcon =
    value === null
      ? CircleHelp
      : isActive
        ? CheckCircle2
        : XCircle;

  return (
    <div
      className="boolean-indicator"
      style={{
        width: "100%",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
        boxSizing: "border-box",
        padding: "8px 12px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
          
    {/* Main status area */}
    <div
      style={{
        flex: "1 1 0",
        minHeight: 0,
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
          maxWidth: "95%",
          padding: "18px 28px",
          borderRadius: "60px",
          border: `1px solid color-mix(in srgb, ${statusColor} 35%, transparent)`,
          backgroundColor: `color-mix(in srgb, ${statusColor} 9%, transparent)`,
        }}
      >
        {/* Status icon */}
        <div
          style={{
            width: "52px",
            height: "52px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            color: statusColor,
            backgroundColor: `color-mix(in srgb, ${statusColor} 16%, transparent)`,
          }}
        >
          <StatusIcon size={34} strokeWidth={2.3} />
        </div>

        {/* Status text */}
        <div
          style={{
            minWidth: 0,
            fontSize: "clamp(20px, 2.5vw, 30px)",
            fontWeight: 700,
            lineHeight: 1.2,
            color: statusColor,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={statusLabel}
        >
          {statusLabel}
        </div>
      </div>
    </div>



      {/* Fixed footer */}
      <div
        style={{
          flex: "0 0 22px",
          height: "22px",
          minHeight: "22px",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          overflow: "hidden",
        }}
      >
        <small
          className="text-muted text-truncate"
          style={{
            fontSize: "11px",
            flexShrink: 0,
          }}
        >
          Input: {value === null ? "—" : value}
        </small>

        <small
          className="text-white text-truncate"
          style={{
            fontSize: "11px",
            textAlign: "right",
            minWidth: 0,
          }}
          title={config.timestamp || ""}
        >
          {config.timestamp || "--"}
        </small>
      </div>
    </div>
  );
}

export default BooleanIndicatorComponent;
